import { Container, DisplayObject, Graphics, Text, TextStyle } from 'pixi.js';
import type { FOSClusterInfo } from '../../../utils/api';

export interface FOSClusterLabelScales {
  x: (value: number) => number;
  y: (value: number) => number;
}

export interface RenderFOSClusterLabelsOptions {
  container: Container;
  clusters: FOSClusterInfo[];
  scales: FOSClusterLabelScales;
  hoveredClusterId?: number | null;
  selectedClusterId?: number | null;
  onClusterHover?: (clusterId: number | null) => void;
  onClusterSelect?: (clusterId: number | null) => void;
}

const LABEL_OFFSET_Y = 18;
const LABEL_PADDING_X = 10;
const LABEL_PADDING_Y = 5;

function createLabelStyles() {
  const labelStyle = new TextStyle({
    fontFamily: 'Inter, system-ui, sans-serif',
    fontSize: 12,
    fontWeight: '500',
    fill: 0x1f2937,
    dropShadow: true,
    dropShadowColor: 0xffffff,
    dropShadowBlur: 4,
    dropShadowDistance: 0,
    dropShadowAlpha: 0.9,
  });

  const activeLabelStyle = new TextStyle({
    fontFamily: 'Inter, system-ui, sans-serif',
    fontSize: 12,
    fontWeight: '500',
    fill: 0x0003AE,
    dropShadow: true,
    dropShadowColor: 0xffffff,
    dropShadowBlur: 4,
    dropShadowDistance: 0,
    dropShadowAlpha: 1,
  });

  const dimmedLabelStyle = new TextStyle({
    fontFamily: 'Inter, system-ui, sans-serif',
    fontSize: 11,
    fontWeight: '500',
    fill: 0x9ca3af,
    dropShadow: true,
    dropShadowColor: 0xffffff,
    dropShadowBlur: 3,
    dropShadowDistance: 0,
    dropShadowAlpha: 0.7,
  });

  return { labelStyle, activeLabelStyle, dimmedLabelStyle };
}

function drawLabelBackground(
  background: Graphics,
  text: Text,
  isActive: boolean,
  alpha: number
): void {
  const bgWidth = text.width + LABEL_PADDING_X * 2;
  const bgHeight = text.height + LABEL_PADDING_Y * 2;
  const cornerRadius = bgHeight / 2;
  const bgColor = isActive ? 0xf0f9ff : 0xffffff;

  background.beginFill(bgColor, alpha);
  background.lineStyle(0.5, isActive ? 0x0003AE : 0xe5e7eb, isActive ? 0.3 : 0.4);
  background.drawRoundedRect(
    -bgWidth / 2,
    -bgHeight / 2,
    bgWidth,
    bgHeight,
    cornerRadius
  );
  background.endFill();
}

function addLabelShadow(labelGroup: Container, text: Text): void {
  const bgWidth = text.width + LABEL_PADDING_X * 2;
  const bgHeight = text.height + LABEL_PADDING_Y * 2;
  const cornerRadius = bgHeight / 2;
  const shadow = new Graphics();

  shadow.beginFill(0x000000, 0.08);
  shadow.drawRoundedRect(
    -bgWidth / 2 + 1,
    -bgHeight / 2 + 2,
    bgWidth,
    bgHeight,
    cornerRadius
  );
  shadow.endFill();
  labelGroup.addChild(shadow as DisplayObject);
}

export function renderFOSClusterLabels({
  container,
  clusters,
  scales,
  hoveredClusterId,
  selectedClusterId,
  onClusterHover,
  onClusterSelect,
}: RenderFOSClusterLabelsOptions): void {
  container.removeChildren();

  const activeClusterId = hoveredClusterId ?? selectedClusterId;
  const { labelStyle, activeLabelStyle, dimmedLabelStyle } = createLabelStyles();

  clusters.forEach((cluster) => {
    const isActive = cluster.cluster_num === activeClusterId;
    const isDimmed = activeClusterId !== null && !isActive;
    const initialStyle = isActive ? activeLabelStyle : isDimmed ? dimmedLabelStyle : labelStyle;

    const labelGroup = new Container();
    labelGroup.x = scales.x(cluster.centroid_x);
    labelGroup.y = scales.y(cluster.centroid_y) - LABEL_OFFSET_Y;

    const text = new Text(cluster.label || `Cluster ${cluster.cluster_num}`, initialStyle);
    text.anchor.set(0.5, 0.5);

    const background = new Graphics();
    drawLabelBackground(background, text, isActive, isActive ? 0.95 : isDimmed ? 0.6 : 0.88);

    if (!isDimmed) {
      addLabelShadow(labelGroup, text);
    }

    labelGroup.addChild(background as DisplayObject);
    labelGroup.addChild(text as DisplayObject);
    labelGroup.alpha = isActive ? 1 : isDimmed ? 0.5 : 0.92;
    labelGroup.interactive = true;
    labelGroup.cursor = 'pointer';

    const updateLabelStyle = (newStyle: TextStyle, newAlpha: number, isActiveState: boolean) => {
      text.style = newStyle;
      background.clear();
      drawLabelBackground(background, text, isActiveState, isActiveState ? 0.95 : 0.88);
      labelGroup.alpha = newAlpha;
    };

    labelGroup.on('pointerover', () => {
      updateLabelStyle(activeLabelStyle, 1, true);
      onClusterHover?.(cluster.cluster_num);
    });

    labelGroup.on('pointerout', () => {
      const isStillSelected = selectedClusterId === cluster.cluster_num;
      updateLabelStyle(
        isStillSelected ? activeLabelStyle : labelStyle,
        isStillSelected ? 1 : 0.92,
        isStillSelected
      );
      onClusterHover?.(null);
    });

    labelGroup.on('pointertap', () => {
      onClusterSelect?.(cluster.cluster_num);
    });

    container.addChild(labelGroup as DisplayObject);
  });
}
